/**
 * @file firebase.ts
 * @description Firebase SDK initialisation for the frontend.
 *
 * Configuration is loaded from Vite environment variables (VITE_FIREBASE_*).
 * Copy .env.example → .env.local and fill in your project values.
 *
 * NEVER commit the .env.local file to version control.
 */

import { initializeApp, getApps, FirebaseApp } from "firebase/app";
import { getAuth, Auth } from "firebase/auth";
import {
  getFirestore,
  Firestore,
  connectFirestoreEmulator,
} from "firebase/firestore";
import { getFunctions, Functions, connectFunctionsEmulator } from "firebase/functions";

// ─────────────────────────────────────────────────────────────────────────────
// Firebase configuration from environment variables
// ─────────────────────────────────────────────────────────────────────────────
export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Prevent duplicate initialisation in Strict Mode / HMR
let app: FirebaseApp;
if (getApps().length === 0) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApps()[0];
}

export const auth: Auth = getAuth(app);
export const db: Firestore = getFirestore(app);
export const functions: Functions = getFunctions(app);

// ─────────────────────────────────────────────────────────────────────────────
// Emulator support (active when VITE_USE_EMULATORS = "true")
// ─────────────────────────────────────────────────────────────────────────────
if (import.meta.env.VITE_USE_EMULATORS === "true") {
  console.info("[Firebase] Connecting to local emulators...");
  connectFirestoreEmulator(db, "localhost", 8080);
  connectFunctionsEmulator(functions, "localhost", 5001);
}
